import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {Animated, Easing, ViewStyle} from 'react-native';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import TextInput from '../textInput';
import {Theme} from '../../theme/lightTheme';
import Label from '../label';
import TextGeneric from '../textGeneric';
import Icon from '../icon';
import {EditItemProps} from '../../interfaces/components/editItem';
import GenericButton from '../button';
import {tr} from '../../translations';

const Container = styled.TouchableOpacity`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;
const ContentContainer = styled.View`
  flex: 1;
  align-items: flex-start;
  justify-content: center;
`;
const ValueTextContainer = styled.View`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;
const Row = styled.View`
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  flex-direction: row;
  margin-top: 3%;
`;
const TextInputContainer = styled.View`
  width: 100%;
`;
const CancelButton = styled(GenericButton)`
  margin-horizontal: 3%;
`;
const SaveButton = styled(GenericButton)``;
const EditIconContainer = styled.TouchableOpacity`
  align-items: center;
  justify-content: center;
`;
const ValueText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s7}px;
`;

/**
 * EditItem component for displaying Editable Item.
 *
 * @param onSavePress
 * @param showEditRightIcon
 * @param rightEditIconName
 * @param onPress
 * @param label
 * @param labelStyle
 * @param value
 * @param {EditItemProps} props - The Edit Item component props.
 * @returns {React.ReactElement} The rendered Edit Item component.
 */
const EditItem: React.FC<EditItemProps> = ({
  onSavePress,
  showEditRightIcon = true,
  rightEditIconName,
  onPress,
  label,
  labelStyle,
  value,
  style,
  isPicker = false,
  disableEdit = false,
  ...props
}): React.ReactElement => {
  const theme = useTheme() as Theme;
  const [isVisible, setIsVisible] = useState(isPicker);

  // Animation values - memoize to prevent recreation
  const animatedValues = useMemo(() => {
    return {
      fadeAnim: new Animated.Value(isPicker ? 1 : 0),
      slideAnim: new Animated.Value(isPicker ? 0 : -15),
      scaleAnim: new Animated.Value(isPicker ? 1 : 0.96),
      valueTextOpacity: new Animated.Value(isPicker ? 0 : 1),
      valueTextScale: new Animated.Value(isPicker ? 0.9 : 1),
      valueTextTranslateY: new Animated.Value(isPicker ? -8 : 0),
    };
  }, [isPicker]);

  // Cleanup animations when component unmounts
  useEffect(() => {
    return () => {
      Animated.timing(animatedValues.fadeAnim, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }).stop();
      Animated.timing(animatedValues.slideAnim, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }).stop();
      Animated.timing(animatedValues.scaleAnim, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }).stop();
      Animated.timing(animatedValues.valueTextOpacity, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }).stop();
      Animated.timing(animatedValues.valueTextScale, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }).stop();
      Animated.timing(animatedValues.valueTextTranslateY, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }).stop();
    };
  }, [animatedValues]);

  // Memoize animations to prevent recreating animation objects on each render
  const runAnimations = useCallback(() => {
    const {
      fadeAnim,
      slideAnim,
      scaleAnim,
      valueTextOpacity,
      valueTextScale,
      valueTextTranslateY,
    } = animatedValues;

    if (isVisible) {
      // Create a single animation group for better performance
      Animated.parallel([
        // Value text animations
        Animated.timing(valueTextOpacity, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
          easing: Easing.out(Easing.cubic),
        }),
        Animated.timing(valueTextScale, {
          toValue: 0.9,
          duration: 220,
          useNativeDriver: true,
          easing: Easing.out(Easing.quad),
        }),
        Animated.timing(valueTextTranslateY, {
          toValue: -8,
          duration: 220,
          useNativeDriver: true,
          easing: Easing.out(Easing.quad),
        }),
        // Form animations - started immediately rather than staggered for better performance
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 7,
          tension: 50,
          useNativeDriver: true,
        }),
        Animated.spring(fadeAnim, {
          toValue: 1,
          friction: 8,
          tension: 65,
          useNativeDriver: true,
        }),
        Animated.spring(slideAnim, {
          toValue: 0,
          friction: 6.5,
          tension: 40,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      // Create a single animation group for better performance
      Animated.parallel([
        // Form animations
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 180,
          useNativeDriver: true,
          easing: Easing.in(Easing.cubic),
        }),
        Animated.timing(scaleAnim, {
          toValue: 0.96,
          duration: 200,
          useNativeDriver: true,
          easing: Easing.inOut(Easing.cubic),
        }),
        Animated.timing(slideAnim, {
          toValue: -15,
          duration: 220,
          useNativeDriver: true,
          easing: Easing.in(Easing.quad),
        }),
        // Value text animations - with a slight delay for sequence
        Animated.sequence([
          Animated.delay(120),
          Animated.parallel([
            Animated.spring(valueTextOpacity, {
              toValue: 1,
              friction: 8,
              tension: 70,
              useNativeDriver: true,
            }),
            Animated.spring(valueTextScale, {
              toValue: 1,
              friction: 7,
              tension: 55,
              useNativeDriver: true,
            }),
            Animated.spring(valueTextTranslateY, {
              toValue: 0,
              friction: 7,
              tension: 50,
              useNativeDriver: true,
            }),
          ]),
        ]),
      ]).start();
    }
  }, [isVisible, animatedValues]);

  // Run animations when visibility changes
  useEffect(() => {
    runAnimations();
  }, [isVisible, runAnimations]);

  // Memoize toggle function to prevent recreating on each render
  const toggleVisibility = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  // Memoize styles to prevent recreation on each render
  const formAnimatedStyle = useMemo((): ViewStyle => {
    return {
      width: '100%' as any, // Type assertion to fix width type issue
      opacity: animatedValues.fadeAnim,
      transform: [
        {translateY: animatedValues.slideAnim},
        {scale: animatedValues.scaleAnim},
      ],
    };
  }, [animatedValues]);

  const valueTextAnimatedStyle = useMemo((): ViewStyle => {
    return {
      width: '100%' as any, // Type assertion to fix width type issue
      opacity: animatedValues.valueTextOpacity,
      transform: [
        {scale: animatedValues.valueTextScale},
        {translateY: animatedValues.valueTextTranslateY},
      ],
    };
  }, [animatedValues]);

  const onSavePressHandler = () => {
    onSavePress && onSavePress();
    setIsVisible(false);
  };

  return (
    <Container
      style={style}
      disabled={!onPress || isPicker || disableEdit}
      onPress={onPress}>
      <ContentContainer
        pointerEvents={onPress || isPicker || disableEdit ? 'none' : 'auto'}>
        {!!label && (
          <Label
            isBold
            label={label}
            labelStyle={{fontSize: theme.text.s6, ...labelStyle}}
          />
        )}

        {isVisible ? (
          <Animated.View style={formAnimatedStyle}>
            <TextInputContainer>
              <TextInput
                value={value || ''}
                labelStyle={{color: theme.secondary}}
                {...props}
              />
              {!isPicker && (
                <Row>
                  <CancelButton
                    outline
                    radius={100}
                    backgroundColor={'transparent'}
                    style={{
                      borderColor: theme.primary + '22',
                      borderWidth: 1,
                    }}
                    isSmall
                    dynamicWidth
                    label={tr('app.cancel')}
                    labelStyle={{
                      minWidth: theme.text.s1 * 2.4,
                      textAlign: 'center',
                    }}
                    onPress={toggleVisibility}
                  />
                  <SaveButton
                    radius={100}
                    isSmall
                    dynamicWidth
                    label={tr('app.save')}
                    labelStyle={{
                      minWidth: theme.text.s1 * 2.4,
                      textAlign: 'center',
                    }}
                    onPress={onSavePressHandler}
                  />
                </Row>
              )}
            </TextInputContainer>
          </Animated.View>
        ) : (
          <Animated.View style={valueTextAnimatedStyle}>
            <ValueTextContainer>
              <ValueText>{value}</ValueText>
              {showEditRightIcon && (
                <EditIconContainer onPress={toggleVisibility}>
                  <Icon
                    name={rightEditIconName || 'edit'}
                    size={theme.text.s4}
                    color={theme.settings.icon}
                  />
                </EditIconContainer>
              )}
            </ValueTextContainer>
          </Animated.View>
        )}
      </ContentContainer>
    </Container>
  );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(EditItem, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.onSavePress === nextProps.onSavePress &&
    prevProps.style === nextProps.style &&
    prevProps.isPicker === nextProps.isPicker &&
    prevProps.disableEdit === nextProps.disableEdit &&
    prevProps.label === nextProps.label
  );
});
